import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { trackPurchase } from "@/lib/analytics";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking" });
  const [attempts, setAttempts] = useState(0);
  const purchaseFired = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", message: "No session_id in URL." });
      return;
    }
    let cancelled = false;
    const maxAttempts = 8;

    const poll = async (n) => {
      try {
        const r = await axios.get(`${API}/payments/stripe/status/${sessionId}`);
        if (cancelled) return;
        setState({ status: "ok", data: r.data });
        setAttempts(n);
        if (r.data.payment_status === "paid" && !purchaseFired.current) {
          purchaseFired.current = true;
          trackPurchase({
            transactionId: sessionId,
            value: (r.data.amount_total || 0) / 100,
            currency: (r.data.currency || "usd").toUpperCase(),
            serviceType: r.data.metadata?.service_type,
            serviceName: r.data.metadata?.service_name,
          });
        }
        if (r.data.payment_status !== "paid" && r.data.status !== "expired" && n < maxAttempts) {
          setTimeout(() => poll(n + 1), 2500);
        }
      } catch (e) {
        if (!cancelled) setState({ status: "error", message: e.message });
      }
    };
    poll(1);
    return () => { cancelled = true; };
  }, [sessionId]);

  const paid = state.data?.payment_status === "paid";
  const expired = state.data?.status === "expired";

  return (
    <div className="min-h-screen bg-[#0D1729] text-white flex items-center justify-center p-6">
      <div className="max-w-2xl w-full hairline bg-[#152138] p-10 lg:p-16" data-testid="payment-success-panel">
        <div className="mono-label mb-6">§ RETAINER · STATUS</div>
        {state.status === "checking" && (
          <>
            <h1 className="font-serif-display text-4xl">Verifying retainer…</h1>
            <p className="text-slate-400 mt-4 text-sm">Confirming on Stripe rail. This typically resolves in a few seconds.</p>
          </>
        )}
        {state.status === "ok" && paid && (
          <>
            <h1 className="font-serif-display text-4xl" data-testid="payment-success-heading">Retainer captured.</h1>
            <p className="text-slate-400 mt-4 text-sm">
              Your $500 Feasibility Audit is now retained. Case has been indexed and the
              48-hour memorandum window begins at the next business day. A privileged memo
              will be delivered to the email on file.
            </p>
            <div className="mt-10 border-t border-slate-800/70 pt-6 grid grid-cols-2 gap-6 text-sm">
              <Stat label="AMOUNT" value={`$${((state.data.amount_total || 0) / 100).toFixed(2)} ${state.data.currency?.toUpperCase()}`} />
              <Stat label="STATUS" value={state.data.payment_status?.toUpperCase()} />
              <Stat label="SERVICE" value={state.data.metadata?.service_name || "—"} />
              <Stat label="SESSION" value={sessionId?.slice(0, 20) + "…"} />
            </div>
          </>
        )}
        {state.status === "ok" && !paid && !expired && (
          <>
            <h1 className="font-serif-display text-4xl">Awaiting confirmation…</h1>
            <p className="text-slate-400 mt-4 text-sm">Polling Stripe rail ({attempts}/8).</p>
          </>
        )}
        {expired && (
          <>
            <h1 className="font-serif-display text-4xl">Session expired.</h1>
            <p className="text-slate-400 mt-4 text-sm">Please re-submit your inquiry to reissue a retainer session.</p>
          </>
        )}
        {state.status === "error" && (
          <>
            <h1 className="font-serif-display text-4xl">Status unavailable.</h1>
            <p className="text-slate-400 mt-4 text-sm">{state.message}</p>
          </>
        )}

        <div className="mt-12">
          <Link to="/" className="btn-secondary" data-testid="back-home-btn">← Return to Index</Link>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="mono-label">{label}</div>
      <div className="font-mono text-white mt-2 break-all">{value || "—"}</div>
    </div>
  );
}
